import {
  Button,
  Label,
  toast,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { catalogApi } from "@/modules/catalog/presentation/api/client";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  moderationApi,
  type UnlistedCatalogProductItem,
  type UnlistedCatalogSkuItem,
} from "../api/client";
import { ListingShopCheckboxPicker } from "../ui/ListingShopCheckboxPicker";

const REASON_LABELS: Record<string, string> = {
  FITMENT_PENDING: "fitment ຍັງບໍ່ຄົບ",
  NO_STOCK: "ບໍ່ມີສະຕ໋ອກ",
  SKU_INACTIVE: "SKU ປິດໃຊ້",
  LISTING_REMOVED: "listing ຖືກລຶບ",
  SHOP_MISMATCH: "ຮ້ານບໍ່ກົງ",
  SKU_NOT_FOUND: "ບໍ່ພົບ SKU",
};

const CONDITION_LABELS: Record<string, string> = {
  new: "ໃໝ່",
  used: "ໃຊ້ແລ້ວ",
  refurb: "ປັບປຸງແລ້ວ",
};

const PRODUCT_PAGE_SIZE = 20;

const MODERATION_LIST_SEARCH = {
  limit: 20,
  offset: 0,
} as const;

function formatProductLabel(item: UnlistedCatalogProductItem) {
  const meta = [item.brandName, item.categoryName].filter(Boolean).join(" · ");
  return meta ? `${item.name} · ${meta}` : item.name;
}

function formatSkuLabel(sku: UnlistedCatalogSkuItem) {
  const oem = sku.oemPartNo?.trim() || sku.id.slice(0, 8);
  const cond = sku.condition
    ? (CONDITION_LABELS[sku.condition] ?? sku.condition)
    : null;
  return cond ? `${oem} · ${cond}` : oem;
}

function productLookupQueryFn() {
  return ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }): Promise<{
    items: UnlistedCatalogProductItem[];
    nextPage: number | null;
  }> => {
    const offset = (pageParam - 1) * PRODUCT_PAGE_SIZE;
    return moderationApi
      .listUnlistedCatalogProducts({
        limit: PRODUCT_PAGE_SIZE,
        offset,
        search: search.trim() || undefined,
      })
      .then((res) => ({
        items: res.data,
        nextPage:
          res.meta.offset + res.data.length < res.meta.total
            ? pageParam + 1
            : null,
      }));
  };
}

async function productPreloadQueryFn(
  id: string,
): Promise<UnlistedCatalogProductItem | null> {
  if (!id) return null;
  try {
    const p = await catalogApi.getProduct(id);
    return {
      id: p.id,
      name: p.name,
      type: p.type,
      status: p.status,
      categoryName: p.categoryName,
      brandName: p.brandName,
    };
  } catch {
    return null;
  }
}

function SkuRadioRow({
  sku,
  checked,
  onSelect,
}: {
  sku: UnlistedCatalogSkuItem;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
        checked
          ? "border-primary/40 bg-primary/5"
          : "bg-muted/20 hover:bg-muted/40"
      }`}
    >
      <input
        type="radio"
        name="catalog-sku"
        checked={checked}
        onChange={onSelect}
        className="accent-primary"
      />
      <span className="font-mono text-sm">{formatSkuLabel(sku)}</span>
    </label>
  );
}

export function CreateListingPage() {
  const nav = useNavigate();
  const [catalogProductId, setCatalogProductId] = useState("");
  const [catalogSkuId, setCatalogSkuId] = useState("");
  const [checkedShopIds, setCheckedShopIds] = useState<string[]>([]);
  const [shopsInitialized, setShopsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skusQuery = useQuery({
    queryKey: ["moderation", "unlisted-skus", catalogProductId],
    queryFn: () =>
      moderationApi.listUnlistedCatalogSkus(catalogProductId, {
        limit: 100,
        offset: 0,
      }),
    enabled: Boolean(catalogProductId),
  });

  const shopsQuery = useQuery({
    queryKey: ["moderation", "catalog-sku-shops", catalogSkuId],
    queryFn: () => moderationApi.listShopsForCatalogSku(catalogSkuId),
    enabled: Boolean(catalogSkuId),
  });

  const shops = shopsQuery.data?.data ?? [];

  useEffect(() => {
    setCatalogSkuId("");
    setCheckedShopIds([]);
    setShopsInitialized(false);
    setError(null);
  }, [catalogProductId]);

  useEffect(() => {
    setCheckedShopIds([]);
    setShopsInitialized(false);
    setError(null);
  }, [catalogSkuId]);

  useEffect(() => {
    if (
      !catalogSkuId ||
      shopsInitialized ||
      shopsQuery.isLoading ||
      !shopsQuery.data
    ) {
      return;
    }
    setCheckedShopIds(
      shopsQuery.data.data.filter((s) => s.hasListing).map((s) => s.shopId),
    );
    setShopsInitialized(true);
  }, [
    catalogSkuId,
    shopsInitialized,
    shopsQuery.isLoading,
    shopsQuery.data,
  ]);

  const shopsToPublish = useMemo(() => {
    const shopMap = new Map(shops.map((s) => [s.shopId, s]));
    return checkedShopIds.filter((shopId) => {
      const shop = shopMap.get(shopId);
      if (!shop) return false;
      if (shop.hasListing) return false;
      return shop.eligible;
    });
  }, [checkedShopIds, shops]);

  const hasNewSelection = shopsToPublish.length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!catalogSkuId || !hasNewSelection) return;

    setError(null);
    setLoading(true);
    try {
      const result = await moderationApi.bulkCreateListings({
        catalogSkuId,
        shopIds: shopsToPublish,
      });

      if (result.failed > 0 && result.created + result.updated === 0) {
        const firstFail = result.results.find(
          (r): r is Extract<typeof r, { ok: false }> => !r.ok,
        );
        const reasons =
          firstFail?.reasons?.map((r) => REASON_LABELS[r] ?? r).join(", ") ??
          firstFail?.error ??
          "Publish ບໍ່ສຳເລັດ";
        setError(reasons);
        return;
      }

      if (result.failed > 0) {
        toast.warning(
          `ສຳເລັດ ${result.created + result.updated} · ລົ້ມເຫຼວ ${result.failed}`,
        );
      } else {
        toast.success(
          result.created > 0
            ? `ສ້າງ listing ${result.created} ລາຍການ`
            : `ອັບເດດ listing ${result.updated} ລາຍການ`,
        );
      }

      const firstOk = result.results.find(
        (r): r is Extract<typeof r, { ok: true }> => r.ok,
      );
      if (firstOk?.listing.id) {
        nav({
          to: "/app/moderation/listings/$id",
          params: { id: firstOk.listing.id },
        });
      } else {
        nav({ to: "/app/moderation", search: MODERATION_LIST_SEARCH });
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: string }).message)
          : "ສ້າງ listing ບໍ່ສຳເລັດ";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການກວດກາ Marketplace", to: "/app/moderation" },
            { label: "ສ້າງ listing" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() =>
              nav({ to: "/app/moderation", search: MODERATION_LIST_SEARCH })
            }
          >
            <ArrowLeftIcon className="size-4" />
          </Button>
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສ້າງ listing</h2>
            <p className="text-muted-foreground text-sm">
              ເລືອກສິນຄ້າ → SKU/OEM → ເລືອກຮ້ານທີ່ຈະ publish
            </p>
          </div>
        </div>

        <form
          className="mx-auto max-w-2xl space-y-6 rounded-xl border bg-card p-6"
          onSubmit={handleSubmit}
        >
          <div className="space-y-2">
            <Label>1. ສິນຄ້າ</Label>
            <InfiniteCombobox<UnlistedCatalogProductItem>
              queryKey={["moderation", "unlisted-products"]}
              queryFn={productLookupQueryFn()}
              preloadQueryFn={productPreloadQueryFn}
              getLabel={formatProductLabel}
              getValue={(item) => item.id}
              value={catalogProductId}
              onValueChange={setCatalogProductId}
              placeholder="ຄົ້ນຫາສິນຄ້າ..."
              clearable
            />
            <p className="text-muted-foreground text-xs">
              ສະແດງເຉພາະສິນຄ້າທີ່ມີ SKU ຍັງບໍ່ມີ listing
            </p>
          </div>

          {catalogProductId ? (
            <div className="space-y-2">
              <Label>2. SKU / OEM</Label>
              {skusQuery.isLoading ? (
                <p className="text-muted-foreground py-3 text-sm">
                  ກຳລັງໂຫຼດ SKU...
                </p>
              ) : (skusQuery.data?.data.length ?? 0) === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-4 text-center text-muted-foreground text-sm">
                  ບໍ່ມີ SKU ທີ່ຍັງບໍ່ມີ listing ສຳລັບສິນຄ້ານີ້
                </p>
              ) : (
                <div className="max-h-52 space-y-1.5 overflow-y-auto pr-0.5">
                  {skusQuery.data?.data.map((sku) => (
                    <SkuRadioRow
                      key={sku.id}
                      sku={sku}
                      checked={catalogSkuId === sku.id}
                      onSelect={() => setCatalogSkuId(sku.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {catalogSkuId ? (
            <div className="space-y-2">
              <Label>3. ຮ້ານ</Label>
              <ListingShopCheckboxPicker
                shops={shops}
                checkedShopIds={checkedShopIds}
                onCheckedChange={setCheckedShopIds}
                isLoading={shopsQuery.isLoading}
              />
              <p className="text-muted-foreground text-xs">
                ຮ້ານທີ່ເຂົ້າຮ່ວມແລ້ວຈະຖືກເລືອກໄວ້ · ເລືອກເພີ່ມຮ້ານໃໝ່ເພື່ອ publish
              </p>
              {hasNewSelection ? (
                <p className="text-primary text-xs">
                  ຈະ publish {shopsToPublish.length} ຮ້ານໃໝ່
                </p>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
              nav({ to: "/app/moderation", search: MODERATION_LIST_SEARCH })
            }
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={loading || !catalogSkuId || !hasNewSelection}
            >
              {loading ? "ກຳລັງດຳເນີນການ..." : "Publish"}
            </Button>
          </div>
        </form>
      </Main>
    </>
  );
}
